import { ComponentRendererMiddleware, CompositionRendererMiddleware } from 'json-react-layouts'

import { ComponentServices } from 'common/routing/page-definition'
import { isFeatureEnabled } from 'common/feature-togglings/features'
import { Features } from './features'
import { TogglesState } from 'common/feature-togglings/reducer'

export type OptionalFeature = undefined | Features | [Features, boolean]

export function isComponentEnabled(toggles: TogglesState, feature: OptionalFeature) {
    if (typeof feature === 'string') {
        if (!isFeatureEnabled(toggles, feature)) {
            return false
        }
    } else if (Array.isArray(feature)) {
        const [featureName, flag] = feature
        if (isFeatureEnabled(toggles, featureName) !== flag) {
            return false
        }
    }

    return true
}

export const componentFeatureToggleMiddleware: ComponentRendererMiddleware<
    ComponentServices,
    {
        /**
         * The feature toggle, or the feature toggle with the state to match
         * @example feature: ['my-feature', true] - render if feature toggle is on
         * @example feature: ['my-feature', false] - render if feature toggle is off
         */
        feature?: Features | [Features, boolean]
    }
> = (componentProps, middlewareProps, services, next) => {
    if (!isComponentEnabled(services.services.store.getState().toggles, middlewareProps.feature)) {
        return null
    }
    return next(componentProps, middlewareProps, services)
}

export const compositionFeatureToggleMiddleware: CompositionRendererMiddleware<
    ComponentServices,
    {
        /**
         * The feature toggle, or the feature toggle with the state to match
         * @example feature: ['my-feature', true] - render if feature toggle is on
         * @example feature: ['my-feature', false] - render if feature toggle is off
         */
        feature?: Features | [Features, boolean]
    }
> = (compositionProps, middlewareProps, services, next) => {
    if (!isComponentEnabled(services.services.store.getState().toggles, middlewareProps.feature)) {
        return null
    }
    return next(compositionProps, middlewareProps, services)
}

export const componentOnEventMiddleware: ComponentRendererMiddleware<ComponentServices, {}> = (
    componentProps,
    middlewareProps,
    serviceProps,
    next
) => {
    const modifiedServices = { ...serviceProps, services: { ...serviceProps.services } }

    modifiedServices.services.onEvent = (event: any) => {
        serviceProps.services.onEvent({
            context: {
                timeStamp: Date.now(),
                componentType: componentProps.componentType,
                componentRenderPath: componentProps.componentRenderPath,
                dataDefinition: componentProps.dataDefinitionArgs,
            },
            ...event,
        })
    }

    return next(componentProps, middlewareProps, modifiedServices)
}
